import { IMsfFormBuilder } from '@components/form-builder/form-builder';
import { MultilingualFieldGenerator } from './multilingual-field-generator';
import { environment } from '@root/src/environments/environment';

export class DataTransformer {

    /**
     * Трансформирует данные формы для отправки на сервер
     */
    static transformForSave(formData: any, modalFields: IMsfFormBuilder[]): any {
        // Сначала применяем многоязычную трансформацию
        let transformedData = MultilingualFieldGenerator.transformDataForSave(formData);

        // Затем применяем типизацию полей
        transformedData = DataTransformer.applyFieldTypes(transformedData, modalFields);

        // Удаляем служебные поля
        transformedData = DataTransformer.removeServiceFields(transformedData);

        return transformedData;
    }

    /**
     * Трансформирует данные с сервера для отображения в форме
     */
    static transformForEdit(item: any, modalFields: IMsfFormBuilder[]): any {
        // Сначала применяем многоязычную трансформацию
        let transformedItem = MultilingualFieldGenerator.transformDataForEdit(item);

        // Затем применяем форматирование для отображения
        transformedItem = DataTransformer.formatForDisplay(transformedItem, modalFields);

        return transformedItem;
    }

    /**
     * Применяет правильные типы данных к полям
     */
    private static applyFieldTypes(data: any, modalFields: IMsfFormBuilder[]): any {
        const result = { ...data };

        modalFields.forEach(field => {
            const fieldName = field.name;
            const fieldType = field.type;
            const value = result[fieldName];

            if (value === null || value === undefined || value === '') {
                // Для пустых значений устанавливаем null для числовых полей
                if (fieldType === 'input-number' || fieldType === 'select') {
                    result[fieldName] = null;
                }
                return;
            }

            switch (fieldType) {
                case 'input-number':
                    result[fieldName] = DataTransformer.parseNumber(value);
                    break;

                case 'checkbox':
                    result[fieldName] = DataTransformer.parseBoolean(value);
                    break;

                case 'date':
                    result[fieldName] = DataTransformer.parseDate(value);
                    break;

                case 'select':
                    // Для select полей проверяем, что это валидный ID
                    if (field.metaField && typeof value === 'string') {
                        // Если это строка, пытаемся преобразовать в GUID
                        result[fieldName] = DataTransformer.parseGuid(value);
                    }
                    break;

                case 'input':
                case 'textarea':
                    // Строковые поля оставляем как есть, но обрезаем пробелы
                    result[fieldName] = typeof value === 'string' ? value.trim() : value;
                    break;
            }
        });

        return result;
    }

    /**
     * Форматирует данные для отображения в форме
     */
    private static formatForDisplay(data: any, modalFields: IMsfFormBuilder[]): any {
        const result = { ...data };

        modalFields.forEach(field => {
            const fieldName = field.name;
            const fieldType = field.type;
            const value = result[fieldName];

            if (value === null || value === undefined) {
                return;
            }

            switch (fieldType) {
                case 'input-number':
                    // Для числовых полей убеждаемся, что это число
                    result[fieldName] = DataTransformer.formatNumber(value);
                    break;

                case 'checkbox':
                    // Для чекбоксов убеждаемся, что это булево значение
                    result[fieldName] = DataTransformer.formatBoolean(value);
                    break;

                case 'date':
                    // Для дат форматируем в нужный формат
                    result[fieldName] = DataTransformer.formatDate(value);
                    break;
            }
        });

        return result;
    }

    /**
     * Удаляет служебные поля из данных
     */
    private static removeServiceFields(data: any): any {
        const result = { ...data };

        // Удаляем служебные поля
        delete result.supportedLanguages;
        if (result.names && result.names.supportedLanguages) {
            delete result.names.supportedLanguages;
        }

        return result;
    }

    /**
     * Парсит число из строки
     */
    private static parseNumber(value: any): number | null {
        if (typeof value === 'number') {
            return isNaN(value) ? null : value;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '') return null;

            const parsed = parseFloat(trimmed);
            return isNaN(parsed) ? null : parsed;
        }

        return null;
    }

    /**
     * Парсит булево значение
     */
    private static parseBoolean(value: any): boolean {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'string') {
            const lower = value.toLowerCase().trim();
            return lower === 'true' || lower === '1' || lower === 'yes' || lower === 'on';
        }

        if (typeof value === 'number') {
            return value !== 0;
        }

        return false;
    }

    /**
     * Парсит дату
     */
    private static parseDate(value: any): string | null {
        if (!value) return null;

        if (value instanceof Date) {
            return value.toISOString();
        }

        if (typeof value === 'string') {
            const date = new Date(value);
            return isNaN(date.getTime()) ? null : date.toISOString();
        }

        return null;
    }

    /**
     * Парсит GUID из строки
     */
    private static parseGuid(value: any): string | null {
        if (!value) return null;

        if (typeof value === 'string') {
            const trimmed = value.trim();
            // Простая проверка на формат GUID
            const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            return guidRegex.test(trimmed) ? trimmed : null;
        }

        return null;
    }

    /**
     * Форматирует число для отображения
     */
    private static formatNumber(value: any): number | null {
        if (typeof value === 'number') {
            return isNaN(value) ? null : value;
        }

        if (typeof value === 'string') {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? null : parsed;
        }

        return null;
    }

    /**
     * Форматирует булево значение для отображения
     */
    private static formatBoolean(value: any): boolean {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'string') {
            return value.toLowerCase() === 'true';
        }

        if (typeof value === 'number') {
            return value !== 0;
        }

        return false;
    }

    /**
     * Форматирует дату для отображения
     */
    private static formatDate(value: any): string | null {
        if (!value) return null;

        if (value instanceof Date) {
            return value.toISOString().split('T')[0]; // YYYY-MM-DD format
        }

        if (typeof value === 'string') {
            const date = new Date(value);
            return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
        }

        return null;
    }

    /**
     * Валидирует данные перед отправкой
     */
    static validateData(data: any, modalFields: IMsfFormBuilder[]): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        // Проверяем мультиязычные поля (names)
        const multilingualFields = modalFields.filter(f => MultilingualFieldGenerator.isMultilingualField(f.name));
        if (multilingualFields.length > 0) {
            const hasAnyMultilingualValue = environment.langs.some(langObj => {
              const lang =  langObj.value;
              const fieldName = MultilingualFieldGenerator.getFieldNameForLanguage('names', lang);
                const value = data[fieldName];
                return value !== null && value !== undefined && value !== '';
            });

            if (!hasAnyMultilingualValue) {
                errors.push('At least one name field (UZ, RU, or EN) must be filled');
            }
        }

        modalFields.forEach(field => {
            const fieldName = field.name;
            const fieldType = field.type;
            const value = data[fieldName];

            // Пропускаем мультиязычные поля, так как они уже проверены выше
            if (MultilingualFieldGenerator.isMultilingualField(fieldName)) {
                return;
            }

            // Проверяем обязательные поля
            if (field.required && (value === null || value === undefined || value === '')) {
                errors.push(`Field "${field.title || fieldName}" is required`);
                return;
            }

            // Проверяем типы данных
            if (value !== null && value !== undefined && value !== '') {
                switch (fieldType) {
                    case 'input-number':
                        if (isNaN(Number(value))) {
                            errors.push(`Field "${field.title || fieldName}" must be a number`);
                        }
                        break;

                    case 'date':
                        if (typeof value === 'string') {
                            const date = new Date(value);
                            if (isNaN(date.getTime())) {
                                errors.push(`Field "${field.title || fieldName}" must be a valid date`);
                            }
                        }
                        break;
                }
            }
        });

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Парсит дату в формате YYYYMMDD (например: 20251013) в Date объект
     * @param dateString - строка даты в формате YYYYMMDD
     * @returns Date объект или null если формат неверный
     */
    static parseDateFromYYYYMMDD(dateString: string): Date | null {
        if (!dateString || typeof dateString !== 'string') {
            return null;
        }

        // Проверяем, что строка имеет длину 8 символов и содержит только цифры
        if (dateString.length !== 8 || !/^\d{8}$/.test(dateString)) {
            return null;
        }

        // Извлекаем год, месяц и день
        const year = parseInt(dateString.substring(0, 4), 10);
        const month = parseInt(dateString.substring(4, 6), 10);
        const day = parseInt(dateString.substring(6, 8), 10);

        // Проверяем валидность даты
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return null;
        }

        // Создаем Date объект (месяц в JavaScript начинается с 0)
        const date = new Date(year, month - 1, day);

        // Проверяем, что дата была создана корректно
        if (isNaN(date.getTime())) {
            return null;
        }

        return date;
    }

    /**
     * Форматирует Date объект в строку формата YYYYMMDD
     * @param date - Date объект
     * @returns строка в формате YYYYMMDD или null
     */
    static formatDateToYYYYMMDD(date: Date): string | null {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return null;
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}${month}${day}`;
    }

    /**
     * Парсит дату в формате MMdd (например: 1013 для 13 октября) в Date объект
     * Использует текущий год
     * @param dateString - строка даты в формате MMdd
     * @param year - год (необязательно, по умолчанию текущий год)
     * @returns Date объект или null если формат неверный
     */
    static parseDateFromMMdd(dateString: string, year?: number): Date | null {
        if (!dateString || typeof dateString !== 'string') {
            return null;
        }

        // Проверяем, что строка имеет длину 4 символа и содержит только цифры
        if (dateString.length !== 4 || !/^\d{4}$/.test(dateString)) {
            return null;
        }

        // Извлекаем месяц и день
        const month = parseInt(dateString.substring(0, 2), 10);
        const day = parseInt(dateString.substring(2, 4), 10);

        // Проверяем валидность месяца и дня
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return null;
        }

        // Используем переданный год или текущий год
        const currentYear = year ?? new Date().getFullYear();

        // Создаем Date объект (месяц в JavaScript начинается с 0)
        const date = new Date(currentYear, month - 1, day);

        // Проверяем, что дата была создана корректно
        if (isNaN(date.getTime())) {
            return null;
        }

        return date;
    }

    /**
     * Форматирует Date объект в строку формата MMdd
     * @param date - Date объект
     * @returns строка в формате MMdd или null
     */
    static formatDateToMMdd(date: Date): string | null {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return null;
        }

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${month}${day}`;
    }
}
